import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QuickLink, QuickLinkType } from '../../../../../../models/quick-link';
import { Page } from '../../../../../../models/page';
import { ApplicationStateService } from '../../../../../../services/application-state.service';
import { PageService } from '../../../../../../services/page.service';
import _ from 'lodash';
import { QuickLinkService } from '../../../../../../services/quick-link.service';
import { ModalService } from '../../../../../../services/modal.service';
import { cloneDeep, delay, forEach, isArray } from 'lodash-es';
import { QuickLinkUtils } from '../../../../../../utils/quick-link-utils';
import { HomeworkService } from '../../../../../../services/homework.service';
import { Assignment } from '../../../../../../models/assignment';
import { Embedded, FitMenuComponent, Interaction, VideoLink } from '@lemberger/fit-menu';
import { CommunicationService } from '../../../../../../services/communication.service';
import { Video } from '../../../../../../models/video';
import { environment } from '../../../../../../../environments/environment';
import { PositioningService } from '../../../../../../services/positioning.service';
import { Position } from '../../../../../../models/position';
import { Subject, Subscription } from 'rxjs';
import { LoadingIndicatorComponent } from '../../../../../utils/loading-indicator/loading-indicator.component';
import { NgClass, NgStyle } from '@angular/common';
import { SidebarService } from '../../../../../../services/sidebar.service';
import { CrudService } from '../../../../../../services/crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';
import { SidebarChangedEvent } from '../../../../../../utils/events/sidebar-changed-event';
import { DigiTrainerComponent } from '../../../../../sidebar/digi-trainer/digi-trainer.component';
import { BookFeatureService } from '../../../../../../services/book-feature.service';

@Component({
    selector: 'app-digi-fit-dropdown',
    templateUrl: './digi-fit-dropdown.component.html',
    styleUrls: ['./digi-fit-dropdown.component.scss'],
    imports: [
        LoadingIndicatorComponent,
        FitMenuComponent,
        NgStyle,
        NgClass
    ],
    standalone: true
})
export class DigiFitDropdownComponent implements OnInit, OnDestroy {
    @Input() quicklink: QuickLink;
    @Input() page: Page;
    @ViewChild('fitMenuContent') fitMenuContent: ElementRef;

    protected readonly Embedded = Embedded;

    isDeactivated: boolean;
    dropdownShownSource = new Subject();
    dropdownShownAnnounced$ = this.dropdownShownSource.asObservable();
    assignment: Assignment;
    loading = false;
    contentPosition: Position;
    displayTop = true;
    subscriptions: Array<Subscription> = [];

    constructor(
        public applicationStateService: ApplicationStateService,
        public pageService: PageService,
        public modalService: ModalService,
        public quicklinkService: QuickLinkService,
        public bookFeatureService: BookFeatureService,
        private homeworkService: HomeworkService,
        private communicationService: CommunicationService,
        private positioningService: PositioningService,
        private sidebarService: SidebarService,
        private crudService: CrudService
    ) {
    }

    private _dropdownShown: boolean;

    get dropdownShown() {
        return this._dropdownShown;
    }

    set dropdownShown(value: boolean) {
        this._dropdownShown = value;
        this.dropdownShownSource.next(value);
        if (value) {
            this.crudService.logDirect(TechnicalFeature.DigiFit);
        }
    }

    @HostListener('window:resize')
    onResize() {
        if (this.dropdownShown) {
            this.dropdownShown = false;
        }
    }

    ngOnDestroy() {
        _.each(this.subscriptions, (subscription) => {
            subscription.unsubscribe();
        });
    }

    ngOnInit(): void {
        this.isDeactivated = _.includes(
            this.applicationStateService.globalVars.disabledAssignments,
            this.quicklink.slug
        );

        this.subscriptions.push(
            this.dropdownShownAnnounced$.subscribe((value) => {
                if (value) {
                    this.setFocus();
                }
            })
        );

        this.subscriptions.push(
            this.communicationService.addHomeworkAnnounced$.subscribe(() => {
                this.dropdownShown = false;
            })
        );
        this.subscriptions.push(
            this.communicationService.viewModeChangeAnnounced$.subscribe(() => {
                this.dropdownShown = false;
            })
        );

        this.subscriptions.push(
            this.communicationService.digiFitMenuOpenAnnounced$.subscribe(
                (pageId: number) => {
                    if (this.quicklink.pageId !== pageId) {
                        this.dropdownShown = false;
                    }
                }
            )
        );
    }

    interacted(data: Interaction) {
        // eslint-disable-next-line default-case
        switch (data.key) {
            case 'trainer':
                this.openDigiTrainer(data.data);
                break;
            case 'eSquirrel':
                this.openESquirrel(data.data);
                break;
            case 'digbi':
                this.openDigbi(data.data);
                break;
            case 'mathe4AllePLUS':
                this.openMathe4Alle(data.data);
                break;
            case 'matheGym':
                this.openMatheGym(data.data);
                break;
            case 'eduvidual':
                this.openLtiLaunch(data.data);
                break;
            case 'eduSuite':
                this.openEduSuite(data.data);
                break;
            case 'mathion':
                this.openMathion(data.data);
                break;
            case 'am':
                this.openAm(data.data);
                break;
            case 'featured':
                this.openQuickLink(data.data);
                break;
            case 'video':
                this.openVideo(data.data);
                break;
            case 'phase6':
                this.openPhase6(data.data);
                break;
            case 'yedo':
                this.openYedo(data.data);
                break;
            case 'mathe4Alle':
            case 'link':
                this.openLink(data.data);
                break;
        }

        this.dropdownShown = false;
    }

    onScroll($event: Event): void {
        $event.stopPropagation();
    }

    setFocus(): void {
        delay(() => {
            this.fitMenuContent?.nativeElement?.querySelector('a')?.focus();
        }, 1500);
    }

    async openDropdown(): Promise<void> {
        if (this.dropdownShown) {
            this.dropdownShown = false;

            return;
        }

        const position = this.positioningService.getPagesContainerSize();
        let top =
            (position.h / 100) *
            (this.quicklink.position.h + this.quicklink.position.y);
        if (top > position.h / 2) {
            top = (position.h / 100) * this.quicklink.position.y;
            position.h = top;
            position.y = -top;
            this.displayTop = false;
        } else {
            position.h = position.h - top;
            position.y = top;
            this.displayTop = true;
        }

        position.w = this.pageService.isCenterPage(this.page)
            ? position.w
            : position.w / 2;
        position.x = -((position.w / 100) * this.quicklink.position.x);
        this.contentPosition = position;

        this.communicationService.openDigiFitMenu(this.quicklink.pageId);

        if (this.assignment) {
            this.dropdownShown = true;

            return;
        }

        if (!this.applicationStateService.bookConfig.quickLinkPrefix?.toLowerCase()) {
            this.dropdownShown = false;

            return;
        }

        const realPageNumber = this.pageService.getRealPageNumber(this.page.index);
        const quicklink = `${this.applicationStateService.bookConfig.quickLinkPrefix?.toLowerCase()}am${realPageNumber}`;
        this.loading = true;

        let assignment: Assignment | null;
        try {
            assignment = await this.quicklinkService.fetchAssignmentData(quicklink);
        } catch (error) {
            assignment = null;
        }

        this.loading = false;

        if (!assignment) {
            this.dropdownShown = false;

            return;
        }

        assignment.feature.fitmenu =
            assignment.feature.fitmenu ??
            assignment.feature.fitmenue ??
            assignment.feature['fit-menue'] ??
            assignment.feature['fit-menu'];

        if (!assignment.feature.fitmenu) {
            this.dropdownShown = false;

            return;
        }

        this.assignment = assignment;
        this.dropdownShown = true;
    }

    isValidFitMenu(): boolean {
        return this.quicklink.link.toLowerCase().includes('digi_fit');
    }

    openVideo(link: VideoLink) {
        const video = new Video();
        video.url = this.prepareUrl(link.file);
        video.title = link.title;
        video.content = video.title;

        if (link.text && !isArray(link.text)) {
            // @ts-ignore
            link.text = [link.text];
        }

        if (link.text) {
            const subtitles = [];
            forEach(link.text, (subtitleSource) => {
                subtitles.push(this.prepareUrl(subtitleSource));
            });

            video.subtitles = subtitles;
        }

        video.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.bookVideo.view;

        this.crudService.logDirect(TechnicalFeature.Video);

        this.modalService.openVideoModal(video);
    }

    openQuickLink(link: string) {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.bypassCheck = false;
        clonedQuicklink.type = QuickLinkType.OTHER;

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openLink(link: string) {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.bypassCheck = true;
        clonedQuicklink.combinedLink = link;
        clonedQuicklink.tmpRedirect = link;
        clonedQuicklink.redirect = link;
        clonedQuicklink.type = QuickLinkType.OTHER;

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openESquirrel(link: string): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link.replace('eSqirrel', 'eSquirrel');
        clonedQuicklink.isSignable = QuickLinkUtils.isSignable(
            clonedQuicklink,
            this.applicationStateService.bookConfig.quickLinkPrefix
        );
        const combinedLink = this.quicklinkService.getCombinedLinkAndTmpRedirect(
            clonedQuicklink.link
        );
        clonedQuicklink.combinedLink = combinedLink.combinedLink;
        clonedQuicklink.tmpRedirect = combinedLink.tmpRedirect;
        clonedQuicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.eSquirrelLinks.view;
        clonedQuicklink.type = QuickLinkType.E_SQUIRREL;
        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openPhase6(link: string): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = QuickLinkUtils.isSignable(
            clonedQuicklink,
            this.applicationStateService.bookConfig.quickLinkPrefix
        );
        const combinedLink = this.quicklinkService.getCombinedLinkAndTmpRedirect(
            clonedQuicklink.link
        );
        clonedQuicklink.combinedLink = combinedLink.combinedLink;
        clonedQuicklink.tmpRedirect = combinedLink.tmpRedirect;
        clonedQuicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.phaseLinks.view;
        clonedQuicklink.type = QuickLinkType.PHASE;
        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openYedo(link: string): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = true;
        clonedQuicklink.combinedLink = undefined;
        clonedQuicklink.tmpRedirect = undefined;
        clonedQuicklink.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.siaLinks.view;
        clonedQuicklink.type = QuickLinkType.YEDO;
        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openEduSuite(link: string): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = true;
        clonedQuicklink.combinedLink = undefined;
        clonedQuicklink.tmpRedirect = undefined;
        clonedQuicklink.bypassCheck = true;
        clonedQuicklink.type = QuickLinkType.EDU_SUITE;
        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openMathion(link: string): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = this.quicklinkService.isSignableLtiConnection(clonedQuicklink);
        clonedQuicklink.combinedLink = undefined;
        clonedQuicklink.tmpRedirect = undefined;
        clonedQuicklink.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.mathion.view;
        clonedQuicklink.type = QuickLinkType.MATHION;

        if (!clonedQuicklink.isSignable) {
            clonedQuicklink.redirect = link;
        }

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openMathe4Alle(link: string) {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = this.quicklinkService.isSignableLtiConnection(clonedQuicklink);
        clonedQuicklink.combinedLink = undefined;
        clonedQuicklink.tmpRedirect = undefined;
        clonedQuicklink.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.mathe4Alle.view;
        clonedQuicklink.type = QuickLinkType.MATHE4ALLE;

        if (!clonedQuicklink.isSignable) {
            clonedQuicklink.redirect = link;
        }

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openMatheGym(link: string) {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = this.quicklinkService.isSignableLtiConnection(clonedQuicklink);
        clonedQuicklink.combinedLink = undefined;
        clonedQuicklink.tmpRedirect = undefined;
        clonedQuicklink.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.matheGym.view;
        clonedQuicklink.type = QuickLinkType.MATHE_GYM;

        if (!clonedQuicklink.isSignable) {
            clonedQuicklink.redirect = link;
        }

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openDigbi(link: string) {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = false;
        clonedQuicklink.combinedLink = undefined;
        clonedQuicklink.tmpRedirect = undefined;
        clonedQuicklink.bypassCheck = true; // No permission needed. Not included in platform ecosystem
        clonedQuicklink.type = QuickLinkType.DIGBI;
        clonedQuicklink.redirect = this.assignment?.feature?.marktplatz;

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openAm(link: string, $event?: Event): void {
        if ($event === undefined) {
            $event = new Event('click');
        }

        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.isSignable = QuickLinkUtils.isSignable(
            clonedQuicklink,
            this.applicationStateService.bookConfig.quickLinkPrefix
        );
        clonedQuicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.hsmLinks.view;
        clonedQuicklink.type = QuickLinkType.HSM;

        if (this.homeworkService.showHomeworkButton) {
            this.homeworkService.openQuicklink(
                clonedQuicklink,
                clonedQuicklink.pageId,
                $event
            );

            return;
        }

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    /**
     * @deprecated since 01.09.2024
     * @param link
     */
    openGMT(link): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.red = link;
        const combinedLink =
            this.quicklinkService.getCombinedLinkAndTmpRedirect(link);
        clonedQuicklink.combinedLink = combinedLink.combinedLink;
        clonedQuicklink.tmpRedirect = combinedLink.tmpRedirect;
        clonedQuicklink.type = QuickLinkType.GMT;
        clonedQuicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.gmtLinks.view;

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openLtiLaunch(link: string): void {
        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.eduvidualLinks.view;
        clonedQuicklink.isSignable = this.quicklinkService.isSignableLtiConnection(clonedQuicklink);
        clonedQuicklink.type = QuickLinkType.OTHER;

        if (!clonedQuicklink.isSignable) {
            clonedQuicklink.redirect = link;
        }

        this.quicklinkService.openQuicklink(clonedQuicklink);
    }

    openAsLink(link: string | Array<string>): void {
        if (isArray(link)) {
            link = link[0];
        }

        const clonedQuicklink = cloneDeep(this.quicklink);
        clonedQuicklink.link = link;
        clonedQuicklink.combinedLink = link;
        clonedQuicklink.type = QuickLinkType.OTHER;
        clonedQuicklink.bypassCheck =
            this.applicationStateService.bookConfig.bookPermissions.externalLinks.view;

        this.quicklinkService.openQuickLinkAsLink(clonedQuicklink);
    }

    async openDigiTrainer(quicklink: string): Promise<void> {
        if (!this.applicationStateService.bookConfig.bookPermissions.exerciseSeries.view) {
            this.modalService.openDisabledDigiTrainerModal();

            return;
        }

        await this.quicklinkService.fetchDigiTrainerContent(quicklink);

        this.sidebarService.triggerSidebarChangeEvent(new SidebarChangedEvent(true, DigiTrainerComponent));

        return;
    }

    private prepareUrl(source: string): string {
        if (
            !source.startsWith('http://') &&
            !source.startsWith('https://') &&
            !source.startsWith('www')
        ) {
            const sourcePrefix = source.split('.');
            let sourceSlug = this.assignment.meta.book + this.assignment.meta.tag;
            if (sourcePrefix.length === 2) {
                sourceSlug = sourcePrefix[0];
            }

            if (sourceSlug === this.assignment.meta.book + this.assignment.meta.tag) {
                sourceSlug = `${this.assignment.meta.book}/${this.assignment.meta.tag}`;
            }

            return `${environment.endpointUrls.assignmentLink}/${sourceSlug}/content/media/${source}`;
        }

        return source;
    }
}
